import React, { useContext, useEffect, useState } from "react";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { ShopContext } from "../Context/ShopContext";
import "./CSS/Checkout.css";

const Checkout = () => {
  const { all_product, cartItems } = useContext(ShopContext);
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initCheckout = async () => {
      setLoading(true);
      setError(null);

      const cartPayload = all_product
        .filter((product) => cartItems[product.id] > 0)
        .map((product) => ({
          id: product.id,
          name: product.color
            ? `${product.name} - ${product.color}`
            : product.name,
          quantity: cartItems[product.id],
          price: product.new_price,
        }));

      if (cartPayload.length === 0) {
        setError("Your cart is empty. Please add items before checkout.");
        setLoading(false);
        return;
      }

      // Read promo from localStorage — set by CartItems on proceed to checkout
      let appliedPromo = null;
      try {
        const stored = localStorage.getItem("appliedPromo");
        if (stored) appliedPromo = JSON.parse(stored);
      } catch (e) {
        console.warn("Could not read promo from localStorage");
      }

      try {
        const response = await fetch(
          "https://backend.mariemadeit.com/create-checkout",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              cart: cartPayload,
              promoCode: appliedPromo?.code || null,
              discountAmount: appliedPromo?.discountAmount || 0,
              discountDescription: appliedPromo?.description || null,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || `Server error (${response.status})`
          );
        }

        const data = await response.json();
        console.log("✅ Checkout initialized:", data);
        setOrderData(data);
        localStorage.setItem("purchasedItems", JSON.stringify(data.cart));
      } catch (err) {
        console.error("❌ Checkout init error:", err);
        setError(err.message || "Failed to initialize checkout");
      } finally {
        setLoading(false);
      }
    };

    initCheckout();
  }, [all_product, cartItems]);

  const createOrder = async (data, actions) => {
    return orderData.orderID;
  };

  const onApprove = async (data, actions) => {
    try {
      const captureResponse = await fetch(
        "https://backend.mariemadeit.com/capture-order",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderID: data.orderID,
            orderNumber: orderData.orderNumber,
            cart: orderData.cart,
          }),
        }
      );

      const captureData = await captureResponse.json();

      if (!captureData.success) {
        throw new Error(captureData.error || "Capture failed");
      }

      localStorage.setItem("currentOrderNumber", captureData.orderNumber);
      localStorage.setItem("customerEmail", captureData.customerEmail);
      localStorage.removeItem("purchasedItems");
      localStorage.removeItem("appliedPromo");

      window.location.href = `/confirmation?orderNumber=${captureData.orderNumber}`;
    } catch (err) {
      console.error("❌ Capture error:", err);
      setError(`Payment capture failed: ${err.message}`);
    }
  };

  const onError = (err) => {
    console.error("❌ PayPal error:", err);
    setError("Payment failed. Please try again.");
  };

  if (loading) {
    return (
      <div className="checkout-container">
        <div className="checkout-loading">
          <p>Initializing checkout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-container">
        <div className="checkout-error">
          <h2>❌ Checkout Error</h2>
          <p>{error}</p>
          <a href="/cart" className="checkout-back-btn">
            Return to Cart
          </a>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="checkout-container">
        <div className="checkout-loading">
          <p>Loading checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-content">
        <h1>Complete Your Purchase</h1>

        <div className="checkout-summary">
          <h2>Order Summary</h2>
          <div className="summary-items">
            {orderData.cart.map((item, index) => (
              <div key={index} className="summary-item">
                <span>{item.name}</span>
                <span>
                  {item.quantity}x ${item.price.toFixed(2)}
                </span>
              </div>
            ))}
          </div>

          <div className="summary-totals">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>${orderData.itemTotal?.toFixed(2) ?? "—"}</span>
            </div>

            {/* Discount row — only shows when promo was applied */}
            {orderData.discountAmount > 0 && (
              <div className="summary-row summary-discount">
                <span>{orderData.discountDescription || "Promo discount"}:</span>
                <span>-${orderData.discountAmount.toFixed(2)}</span>
              </div>
            )}

            {orderData.hasPhysical && (
              <div className="summary-row">
                <span>Shipping:</span>
                <span>${orderData.shippingAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row">
              <span>Tax:</span>
              <span>${orderData.taxAmount.toFixed(2)}</span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <span>${orderData.totalAmount.toFixed(2)}</span>
            </div>
          </div>

          {!orderData.hasPhysical && (
            <p className="digital-note">
              📥 No shipping required — digital items will be sent to your email
            </p>
          )}
        </div>

        <div className="paypal-section">
          <PayPalButtons
            createOrder={createOrder}
            onApprove={onApprove}
            onError={onError}
            style={{ layout: "vertical", color: "silver", shape: "pill" }}
          />
        </div>

        <a href="/cart" className="checkout-back-btn">
          ← Return to Cart
        </a>
      </div>
    </div>
  );
};

export default Checkout;